import firebase from '../firebase'
import { getUser } from './auth'
import 'firebase/compat/firestore';

import { db, authReady } from '../firebase';

const normalizeFabricValue = (value) => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

const getFabricUpdatedTime = (fabricItem) => {
    const timestamp = fabricItem?.updatedAt || fabricItem?.lastUpdated || fabricItem?.modifiedAt || fabricItem?.createdAt;
    if (!timestamp) return 0;
    if (typeof timestamp.toMillis === "function") return timestamp.toMillis();
    if (typeof timestamp.seconds === "number") return timestamp.seconds * 1000;
    const parsed = new Date(timestamp).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
};

const getFabricDedupeKeys = (fabricItem) => {
    const keys = [];
    const id = String(fabricItem?.fabricID ?? fabricItem?.id ?? "").trim();
    const fabricName = normalizeFabricValue(fabricItem?.fabric || fabricItem?.name);
    const brand = normalizeFabricValue(fabricItem?.brand);

    if (id) keys.push(`id:${id}`);
    if (fabricName && brand) keys.push(`name:${brand}:${fabricName}`);

    return keys;
};

const preferFabricRecord = (current, next) => {
    if (!current) return next;
    if (next?.__masterSynced && !current?.__masterSynced) return next;
    if (current?.__masterSynced && !next?.__masterSynced) return current;

    const currentTime = getFabricUpdatedTime(current);
    const nextTime = getFabricUpdatedTime(next);
    if (nextTime !== currentTime) return nextTime > currentTime ? next : current;

    if (!current?.fabricImg && next?.fabricImg) return next;
    if ((!current?.src || current.src.length === 0) && next?.src) return next;

    return next;
};

export const dedupeFabricList = (fabricList) => {
    if (!Array.isArray(fabricList)) return [];

    const results = [];
    const keyToIndex = new Map();

    fabricList.forEach((fabricItem) => {
        const keys = getFabricDedupeKeys(fabricItem);
        const existingIndex = keys.map((key) => keyToIndex.get(key)).find((index) => index !== undefined);

        if (existingIndex === undefined) {
            const index = results.length;
            results.push(fabricItem);
            keys.forEach((key) => keyToIndex.set(key, index));
            return;
        }

        const preferred = preferFabricRecord(results[existingIndex], fabricItem);
        results[existingIndex] = preferred;
        getFabricDedupeKeys(preferred).forEach((key) => keyToIndex.set(key, existingIndex));
    });

    return results;
};

const mergeFabricWithMaster = (fabricItem, masterItem) => {
    if (!fabricItem || !masterItem) return fabricItem;

    const merged = { ...fabricItem, __masterSynced: true };
    const fieldsToSync = [
        "fabric",
        "fabricImg",
        "brand",
        "brandImg",
        "price",
        "discount",
        "material",
        "color",
        "pattern",
        "weave",
        "width",
        "occasion",
        "recommended_buttons",
        "default_recommended_button"
    ];

    fieldsToSync.forEach((field) => {
        const value = masterItem[field];
        if (value !== undefined && value !== null && value !== "") {
            merged[field] = value;
        }
    });

    if (masterItem.name) {
        merged.fabric = masterItem.name;
    }

    if (!masterItem.fabricImg && typeof masterItem.src === "string" && masterItem.src.trim().length > 0) {
        merged.fabricImg = masterItem.src;
    }

    if (Array.isArray(masterItem.src) && masterItem.src.length > 0) {
        merged.src = masterItem.src;
    }

    return merged;
};


export const getData = async (Collectionref) => {
    try {
      await authReady();
      var data = await Collectionref.get();
      var Data = [];
      data.forEach((element) => {
        Data.push({...element.data(), ...{"doc":element.id}});
      });
      return Data;
    } catch (error) {
      // console.log(error);
    }
};

// --- Lightweight cache for frequently fetched customizer collections ---
const CUSTOMIZER_CACHE_TTL_MS = 5 * 60 * 1000;
const customizerDataCache = new Map();
const customizerPromiseCache = new Map();

const readCustomizerCache = (key) => {
    const entry = customizerDataCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.time > CUSTOMIZER_CACHE_TTL_MS) {
        customizerDataCache.delete(key);
        return null;
    }
    return entry.value;
};

const writeCustomizerCache = (key, value) => {
    customizerDataCache.set(key, { value, time: Date.now() });
};

const withCustomizerCache = (key, loader) => {
    const cached = readCustomizerCache(key);
    if (cached !== null && cached !== undefined) return Promise.resolve(cached);

    if (customizerPromiseCache.has(key)) {
        return customizerPromiseCache.get(key);
    }

    const promise = Promise.resolve()
        .then(loader)
        .then((value) => {
            if (value !== undefined && value !== null) {
                writeCustomizerCache(key, value);
            }
            return value;
        })
        .finally(() => {
            customizerPromiseCache.delete(key);
        });

    customizerPromiseCache.set(key, promise);
    return promise;
};

export const clearCustomizerCache = (key = null) => {
    if (!key) {
        customizerDataCache.clear();
        customizerPromiseCache.clear();
        return;
    }
    customizerDataCache.delete(key);
    customizerPromiseCache.delete(key);
};

export const getFabricDetails = async(types,category,  fabric = 0)=> withCustomizerCache(
    `fabricDetails:${category}:${(types || []).join(",")}:${fabric}`,
    async () => {

    try{
        const coll  = category === "suit" ? "Suits" 
                    : category === "formal" || category === "pant" || category === "shirt" ? "formal"
                    : category === "kurta" || category === "only_kurta" || category === "ethnic" || category === "pajama" ? "kurta" 
                    : category === "blazer" ? "blazer"
                    : category === "sadri" ? "kurta"
                    : "dummy" ;
        
        console.log("Getting fabrics for :", coll)

        const DOC  = db.collection('Fabric').doc(coll)

        const getFallbackTypeNames = (typeName) => {
            const normalizedType = String(typeName || "").toLowerCase();
            const fallbackNames = [typeName];

            if (normalizedType) {
                fallbackNames.push(normalizedType);
                fallbackNames.push(normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1));
            }

            if (coll === "kurta") {
                fallbackNames.push("Kurta", "kurta", "fabrics", "Fabrics");
            }

            return [...new Set(fallbackNames.filter(Boolean))];
        };

        // Fetch all types in parallel; per-type fallbacks remain sequential
        const perTypeResults = await Promise.all(
            (types || []).map(async (typeName) => {
                const fallbackNames = getFallbackTypeNames(typeName);
                let res = [];
                for (let j = 0; j < fallbackNames.length; j += 1) {
                    const collection = DOC.collection(fallbackNames[j]);
                    res = await getData(collection);
                    if (Array.isArray(res) && res.length > 0) break;
                }
                return [typeName, Array.isArray(res) ? res : []];
            })
        );
        const Data = {};
        perTypeResults.forEach(([typeName, res]) => { Data[typeName] = res; });

        const fabricIds = [];
        Object.keys(Data).forEach((typeKey) => {
            (Data[typeKey] || []).forEach((item) => {
                const id = item?.fabricID ?? item?.id;
                if (id !== undefined && id !== null && id !== "") {
                    fabricIds.push(id);
                }
            });
        });

        if (fabricIds.length > 0) {
            const uniqueIds = [...new Set(fabricIds)];
            const latestFabrics = await getDocsFromQueryForFabrics(null, uniqueIds);
            const latestById = new Map(
                (latestFabrics || []).map((item) => [String(item?.id ?? item?.fabricID), item])
            );

            Object.keys(Data).forEach((typeKey) => {
                Data[typeKey] = dedupeFabricList((Data[typeKey] || []).map((item) => {
                    const id = String(item?.fabricID ?? item?.id ?? "");
                    const latest = latestById.get(id);
                    return mergeFabricWithMaster(item, latest);
                }));
            });
        } else {
            Object.keys(Data).forEach((typeKey) => {
                Data[typeKey] = dedupeFabricList(Data[typeKey]);
            });
        }

        if(fabric === 1){
            
        }
        return Data
    }catch(e){
        // console.log("from getDetails-firestore ", e);
    }
});

const fetchAllButtons = () => withCustomizerCache("buttons:all", async () => {
    const collection = db.collection("Buttons");
    return (await getData(collection)) || [];
});

export const getButtons = async(category, targetType = null)=>{
    try{
        const res = await fetchAllButtons();
        const normalize = (value) => String(value || "").trim().toLowerCase();
        const normalizeCategory = (value) => {
            const v = normalize(value);
            if (v === "suits") return "suit";
            if (v === "only_kurta" || v === "sadri") return "kurta";
            return v;
        };
        const flowCategory = normalizeCategory(category);
        const flowType = normalize(targetType);
        return (res || []).filter((button) => {
            const parentCategory = normalizeCategory(button?.parentCategory);
            const buttonTargetType = normalize(button?.targetType);
            const parentMatch = !parentCategory || parentCategory === flowCategory;
            const typeMatch = !flowType || !buttonTargetType || buttonTargetType === flowType;
            return parentMatch && typeMatch;
        });
    } catch(e){
        console.log("from getButtons-firestore ", e);
    }

}

export const getTuxedo = async (category) => withCustomizerCache(`tuxedo:${category}`, async () => {
    try {
        const coll  = category === "suit" ? "Suits" 
                    : category === "formal" || category === "pant" || category === "shirt" ? "formal"
                    : category === "kurta" || category === "only_kurta" || category === "ethnic" ? "kurta" 
                    : category === "blazer" ? "blazer"
                    : category === "sadri" ? "kurta"
                    : "dummy" ;

        const collection = db.collection('Fabric').doc(coll).collection('tuxedo');
        const res = await getData(collection);
        return res;
    } catch (e) {
        console.log("from getTuxedo-firestore ", e);
    }
});

export const getEmbroidery = async (category) => withCustomizerCache(`embroidery:${category}`, async () => {
    try {
        const inferEmbroideryTargetType = (item, fallbackType) => {
            const explicitType = String(item?.targetType || item?._targetType || "").trim().toLowerCase();
            if (explicitType) return explicitType;
            const values = Array.isArray(item?.values) ? item.values : [];
            const valueTypes = values.map((value) => String(value?.targetType || value?.type || value?.refPath || "").toLowerCase());
            if (valueTypes.some((value) => value.includes("sadri"))) return "sadri";
            if (valueTypes.some((value) => value.includes("coat"))) return "coat";
            if (valueTypes.some((value) => value.includes("kurta"))) return "kurta";
            return fallbackType;
        };
        const normalizeEmbroideryCollection = (item, targetCategory, clothType) => {
            const targetType = inferEmbroideryTargetType(item, clothType);
            return {
                ...item,
                targetCategory: item.targetCategory || targetCategory,
                targetType: item.targetType || targetType,
                _targetCategory: targetCategory,
                _targetType: targetType,
            };
        };

        const targetCategory = category === "suit" ? "suits"
            : (category === "kurta" || category === "only_kurta") ? "kurta"
            : category === "blazer" ? "blazer"
            : category === "sadri" ? "kurta"
            : "";
        const targetCategories = category === "sadri" ? ["kurta", "sadri"] : [targetCategory];
        const enabledTypes = category === "sadri" ? ["sadri"] : category === "blazer" ? ["coat"] : ["coat", "kurta", "sadri"];

        if (targetCategory) {
            // Fetch all cloth-type embroidery collections in parallel
            const fetched = await Promise.all(targetCategories.flatMap((targetCategoryKey) => enabledTypes.map(async (clothType) => {
                try {
                    const collRef = db
                        .collection("Fabric")
                        .doc("embroidery")
                        .collection(targetCategoryKey)
                        .doc(clothType)
                        .collection("collections");
                    const data = await getData(collRef);
                    return (data || []).map((item) => normalizeEmbroideryCollection(item, targetCategoryKey, clothType));
                } catch (e) {
                    console.log(`getEmbroidery: missing path for ${targetCategoryKey}/${clothType}`);
                    return [];
                }
            })));
            const collections = fetched.flat();
            if (collections.length > 0) {
                return collections.sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
            }
        }

        // Backward compatibility with old structure.
        let coll  = category === "suit" ? "Suits"
                    : category === "formal" || category === "pant" || category === "shirt" ? "formal"
                    : (category === "kurta" || category === "only_kurta") ? "kurta"
                    : category === "blazer" ? "blazer"
                    : category === "sadri" ? "kurta"
                    : "dummy" ;

        coll = coll + "_collections";
        const legacyCollection = db.collection("Fabric").doc("embroidery").collection(coll);
        const legacyData = await getData(legacyCollection);
        const normalizedLegacyData = (legacyData || []).map((item) => normalizeEmbroideryCollection(item, coll.replace("_collections", ""), category === "sadri" ? "sadri" : ""));
        if (category === "sadri") {
            return normalizedLegacyData.filter((item) => String(item?.targetType || item?._targetType || "").toLowerCase() === "sadri");
        }
        return normalizedLegacyData;
    } catch (e) {
        console.log("from getEmbroidery-firestore ", e);
    }
});

export const getEmbroideryStyle = async () => withCustomizerCache("embroideryStyle:all", async () => {
    try {
        console.log("get emb styles");
        const collection = db.collection('Fabric').doc('embroidery').collection("styles");
        const res = await getData(collection);
        return res;
    } catch (e) {
        console.log("from getEmbroidery style-firestore ", e);
    }
});

const docDataCache = new Map();
const docPromiseCache = new Map();

export const clearDocDataCache = (docPath = null) => {
    if (!docPath) {
        docDataCache.clear();
        docPromiseCache.clear();
        return;
    }
    docDataCache.delete(docPath);
    docPromiseCache.delete(docPath);
};

export const getDatafromDoc = async (docref) => {
    const cacheKey = docref?.path || null;
    if (cacheKey && docDataCache.has(cacheKey)) {
        return docDataCache.get(cacheKey);
    }

    if (cacheKey && docPromiseCache.has(cacheKey)) {
        return docPromiseCache.get(cacheKey);
    }

    try {
      const loadPromise = authReady()
        .then(() => docref.get())
        .then((data) => {
            const resolved = data.data() || { "src": "" };
            if (cacheKey) {
                docDataCache.set(cacheKey, resolved);
            }
            return resolved;
        })
        .catch(() => ({ "src": "" }))
        .finally(() => {
            if (cacheKey) {
                docPromiseCache.delete(cacheKey);
            }
        });

      if (cacheKey) {
        docPromiseCache.set(cacheKey, loadPromise);
      }

      return await loadPromise;
    } catch (error) {
      // console.log(error);
      return {"src":""}
    }
};

export const getDataFromCollection=async(Collection, typ=0)=>{
    try{
        if(typ === 0){

        }else{
            const user = getUser()

            if(user){
                // const email = user.email
                let usrid = user.email
                if(!usrid){
                    usrid = user.phoneNumber
                }
                const res = await db.collection("Users").doc(usrid).collection(Collection).get()

                return {
                    "status":true,
                    "res":res
                }
            }else{
                return {
                    "status":false,
                    "error":"User not found"
                }
            }
            
        }
    }catch(error){
        // console.log(error)
        return {
            "status":false,
            "error":"Error from DB" + error
        }
    }
}

export const getDocOnCondition = async(collection, attr, val)=>{
    try{
        // A first visit's anonymous sign-in may not have settled yet.
        await authReady();
        const docs = await collection.where(attr, "==", val).get()
        let Docs = []
        docs.forEach((element) => {
            Docs.push({...element.data(), ...{"doc":element.id}});
        });
        return Docs
    }catch(e){
      // console.log("fetching doc names", e)
    }
  }

        export const getDocsFromQueryForFabrics = async(collectionRef, idsArray) =>{
    try{
        const results = [];
                if(!Array.isArray(idsArray) || idsArray.length === 0){
                        return results;
                }
            const queryValues = [];
            idsArray.forEach((id) => {
                if (id === undefined || id === null || id === "") return;
                queryValues.push(id);
                const strId = String(id);
                queryValues.push(strId);
                const numericId = Number(strId);
                if (!Number.isNaN(numericId)) {
                    queryValues.push(numericId);
                }
            });

            const uniqueQueryValues = [];
            const seen = new Set();
            queryValues.forEach((id) => {
                const key = `${typeof id}:${String(id)}`;
                if (seen.has(key)) return;
                seen.add(key);
                uniqueQueryValues.push(id);
            });

            const sourceCollection = collectionRef || db.collection("Fabric").doc("Suits").collection("fabrics");
            const cacheKey = `fabricQuery:${sourceCollection.path}:${uniqueQueryValues.map((id) => `${typeof id}:${String(id)}`).sort().join("|")}`;
            return await withCustomizerCache(cacheKey, async () => {
        const chunkSize = 10;
        // Split into chunks of 10 due to Firestore 'in' query limitation
            for (let i = 0; i < uniqueQueryValues.length; i += chunkSize) {
                const chunk = uniqueQueryValues.slice(i, i + chunkSize);
                const querySnapshot = await sourceCollection.where("id", "in", chunk).get();
                querySnapshot.forEach((doc) => {
                    results.push({...doc.data(), doc: doc.id});
            });
        }

                return dedupeFabricList(results);
                        });
    } catch(e){
      console.log("fetching doc from query", e)
    }
  }